import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  PencilIcon,
  TrashIcon,
  PrinterIcon, // Add PrinterIcon
  ArrowPathIcon
  // Removed DocumentArrowDownIcon as it's replaced
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface QuoteItem {
  id: number;
  structureId: number;
  startDate: string;
  endDate: string;
  price: number;
  discount: number;
  total: number;
  Structure?: {
    id: number;
    code: string;
    type: string;
    name?: string;
    address?: string;
  };
}

interface Quote {
  id: number;
  quoteNumber: string;
  clientId: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted';
  totalAmount: number;
  discountAmount: number;
  finalAmount: number;
  quoteDate: string;
  validUntil: string;
  notes?: string;
  termsAndConditions?: string;
  convertedToOrderId?: number;
  createdAt: string;
  updatedAt: string;
  Client?: {
    id: number;
    name: string;
    email?: string;
    phone?: string;
  };
  QuoteItems?: QuoteItem[];
}

const QuoteList: React.FC = () => {
  const { can } = useAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    fetchQuotes();
  }, []);
  
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/quotes');
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setError('Ошибка при загрузке коммерческих предложений');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle quote deletion
  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить это коммерческое предложение?')) {
      try {
        await axios.delete(`/quotes/${id}`);
        setQuotes(quotes.filter(quote => quote.id !== id));
      } catch (error) {
        console.error('Error deleting quote:', error);
        alert('Ошибка при удалении коммерческого предложения');
      }
    }
  };
  
  // Handle quote conversion to order
  const handleConvertToOrder = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите конвертировать это предложение в заказ?')) {
      try {
        const response = await axios.post(`/quotes/${id}/convert`);
        
        // Refresh quotes list after conversion
        fetchQuotes();
        
        // Redirect to the new order
        if (response.data.orderId) {
          window.location.href = `/orders/${response.data.orderId}`;
        }
      } catch (error) {
        console.error('Error converting quote to order:', error);
        alert('Ошибка при конвертации в заказ');
      }
    }
  };

  // Open printable view for quote
  const handleOpenPrintView = (id: number) => {
    // Construct the URL for the print page
    const printUrl = `/quotes/${id}/print`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Filter quotes by search term and status
  const filteredQuotes = quotes.filter(quote => {
    // Status filter
    if (statusFilter !== 'all' && quote.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      quote.quoteNumber.toLowerCase().includes(searchLower) ||
      (quote.Client?.name && quote.Client.name.toLowerCase().includes(searchLower)) ||
      String(quote.id).includes(searchTerm)
    );
  });
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      case 'converted':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return 'Черновик';
      case 'sent': return 'Отправлено';
      case 'accepted': return 'Принято';
      case 'rejected': return 'Отклонено';
      case 'expired': return 'Истекло';
      case 'converted': return 'Конвертировано';
      default: return status;
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Коммерческие предложения"
        subtitle="Управление ценовыми предложениями для клиентов"
        actions={
          can('quotes.create') ? (
            <Link
              to="/quotes/new"
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Создать КП
            </Link>
          ) : null
        }
      />
      
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}
      
      {/* Filters */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end space-y-4 md:space-y-0 md:space-x-4">
        {/* Search box */}
        <div className="w-full md:w-64">
          <label htmlFor="search" className="form-label">Поиск</label>
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              id="search"
              type="text"
              placeholder="Номер КП, клиент..."
              className="form-input pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        {/* Status filter */}
        <div className="w-full md:w-48">
          <label htmlFor="status-filter" className="form-label">Статус</label>
          <select
            id="status-filter"
            className="form-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Все статусы</option>
            <option value="draft">Черновик</option>
            <option value="sent">Отправлено</option>
            <option value="accepted">Принято</option>
            <option value="rejected">Отклонено</option>
            <option value="expired">Истекло</option>
            <option value="converted">Конвертировано</option>
          </select>
        </div>
        
        {/* Refresh button */}
        <button
          type="button"
          onClick={fetchQuotes}
          className="btn-outline flex items-center"
        >
          <ArrowPathIcon className="h-5 w-5 mr-1" />
          Обновить
        </button>
      </div>
      
      {/* Quotes table */}
      {filteredQuotes.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'Коммерческие предложения не найдены' 
              : 'Нет коммерческих предложений'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    № КП
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Клиент
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дата
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сумма
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    {/* Removed whitespace-nowrap, added break-words */}
                    <td className="px-6 py-4 break-words">
                      <Link to={`/quotes/${quote.id}`} className="text-primary hover:text-primary-dark">
                        {quote.quoteNumber || `КП-${quote.id}`}
                      </Link>
                    </td>
                    {/* Removed whitespace-nowrap, added break-words */}
                    <td className="px-6 py-4 break-words">
                      {quote.Client ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {quote.Client.name}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    {/* Removed whitespace-nowrap */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(quote.quoteDate || quote.createdAt)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Действует до: {quote.validUntil ? formatDate(quote.validUntil) : '-'}
                      </div>
                    </td>
                    {/* Removed whitespace-nowrap */}
                    <td className="px-6 py-4">
                      <span className={`px-2 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusBadgeClass(quote.status)}`}>
                        {getStatusText(quote.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      {formatCurrency(quote.finalAmount)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenPrintView(quote.id)} // Use new handler
                          className="text-blue-600 hover:text-blue-900"
                          title="Печать КП" // Update title
                        >
                          <PrinterIcon className="h-5 w-5" /> {/* Use PrinterIcon */}
                        </button>

                        {quote.status !== 'converted' && can('orders.create_from_quote') && ( // New permission
                          <button
                            onClick={() => handleConvertToOrder(quote.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Создать заказ"
                          >
                            <ArrowPathIcon className="h-5 w-5" />
                          </button>
                        )}
                        
                        {quote.status === 'draft' && can('quotes.edit') && (
                          <Link
                            to={`/quotes/edit/${quote.id}`}
                            className="text-gray-600 hover:text-gray-900"
                            title="Редактировать"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </Link>
                        )}
                        {quote.status === 'draft' && can('quotes.delete') && (
                          <button
                            onClick={() => handleDelete(quote.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Удалить"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteList;
